import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ChevronDown, ChevronUp, Download, Plus, X } from "lucide-react";
import type { ExportColumn, ExportFieldKey } from "@guestbook/shared";
import { DEFAULT_EXPORT_COLUMNS } from "@guestbook/shared";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Button } from "@/shared/components/ui/Button";
import { Checkbox } from "@/shared/components/ui/Checkbox";
import { FormField } from "@/shared/components/form/FormField";
import { Input } from "@/shared/components/ui/Input";
import { Label } from "@/shared/components/ui/Label";
import { SelectInput } from "@/shared/components/ui/SelectInput";
import { FIELD_KEYS, FIELD_LABELS, useExportCsv, useExportPreview } from "@/shared/lib/export";
import { useActiveScope, useScopedHref } from "@/shared/lib/scope";
import { cn } from "@/shared/lib/utils";

const FIELD_OPTIONS = FIELD_KEYS.map((key) => ({ value: key, label: FIELD_LABELS[key] }));

function newColumn(): ExportColumn {
  return { fields: ["contact.name"], header: "New column", separator: " ", visible: true };
}

/** Debounces a value so the preview query doesn't refetch on every keystroke. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function ExportPage() {
  const { listId, list } = useActiveScope();
  const [columns, setColumns] = useState<ExportColumn[]>(() =>
    DEFAULT_EXPORT_COLUMNS.map((c) => ({ ...c, fields: [...c.fields] })),
  );
  const href = useScopedHref();
  const exportCsv = useExportCsv();
  const debouncedColumns = useDebouncedValue(columns, 300);
  const preview = useExportPreview({ columns: debouncedColumns, listId });

  const scopeLabel = listId ? list?.name ?? "Selected list" : "All Contacts";
  const exportTitle = listId
    ? `Export Contacts from ${scopeLabel}`
    : `Export ${scopeLabel}`;
  const visibleCount = columns.filter((c) => c.visible).length;

  function updateColumn(index: number, patch: Partial<ExportColumn>) {
    setColumns((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }
  function moveColumn(index: number, dir: -1 | 1) {
    setColumns((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (!moved) return prev;
      next.splice(target, 0, moved);
      return next;
    });
  }
  function removeColumn(index: number) {
    setColumns((prev) => prev.filter((_, i) => i !== index));
  }
  function addColumn() {
    setColumns((prev) => [...prev, newColumn()]);
  }

  return (
    <Section spacing="md">
      <Container size="4xl">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <Link
              to={href("/app/contacts")}
              className="type-label-sm tracking-wider uppercase text-text-muted hover:text-text"
            >
              ← Back to contacts
            </Link>
            <h1 className="type-heading-lg text-text">{exportTitle}</h1>
            <p className="type-body-sm text-text-muted">
              Build a CSV for mailing. Household members export their household's
              mailing address. Toggle, rename, reorder, and combine fields into a
              single column.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {columns.map((column, index) => (
              <ColumnRow
                key={index}
                column={column}
                index={index}
                isFirst={index === 0}
                isLast={index === columns.length - 1}
                onChange={updateColumn}
                onMove={moveColumn}
                onRemove={removeColumn}
              />
            ))}
          </div>

          <Button variant="secondary" className="w-full" onClick={addColumn}>
            <Plus aria-hidden className="h-4 w-4" />
            Add column
          </Button>

          {visibleCount > 0 ? (
            <section className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="type-heading-sm text-text">Preview</h2>
                <p className="type-body-sm text-text-muted">
                  How the first few records in {scopeLabel} will export, with
                  household name and address applied.
                </p>
              </div>
              <PreviewTable
                isLoading={preview.isLoading}
                error={preview.isError ? preview.error.message : null}
                headers={preview.data?.headers ?? []}
                rows={preview.data?.rows ?? []}
              />
            </section>
          ) : null}

          {visibleCount === 0 ? (
            <p className="type-body-sm text-text-muted">
              Enable at least one column to export.
            </p>
          ) : null}

          <Button
            className="w-full"
            onClick={() => exportCsv.mutate({ columns, listId })}
            disabled={exportCsv.isPending || visibleCount === 0}
          >
            <Download aria-hidden className="h-4 w-4" />
            {exportCsv.isPending ? "Preparing…" : "Download CSV"}
          </Button>
          {exportCsv.isError ? (
            <p className="type-body-sm text-danger-strong">
              Couldn't export: {exportCsv.error.message}
            </p>
          ) : null}
        </div>
      </Container>
    </Section>
  );
}

interface PreviewTableProps {
  isLoading: boolean;
  error: string | null;
  headers: string[];
  rows: string[][];
}

function PreviewTable({ isLoading, error, headers, rows }: PreviewTableProps) {
  if (error) {
    return (
      <p className="type-body-sm text-danger-strong">Couldn't load preview: {error}</p>
    );
  }
  if (isLoading) {
    return <p className="type-body-sm text-text-muted">Loading preview…</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="type-body-sm text-text-muted">
        No contacts in this scope to preview.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-border-muted glass-surface-subtle">
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-border-muted">
            {headers.map((header, i) => (
              <th
                key={i}
                className="whitespace-nowrap px-4 py-2.5 type-label-sm uppercase tracking-wider text-text-muted"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={r} className="border-t border-border-muted first:border-t-0">
              {row.map((cell, c) => (
                <td
                  key={c}
                  className="whitespace-nowrap px-4 py-2.5 type-body-sm text-text"
                >
                  {cell || <span className="text-text-muted">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ColumnRowProps {
  column: ExportColumn;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  onChange: (index: number, patch: Partial<ExportColumn>) => void;
  onMove: (index: number, dir: -1 | 1) => void;
  onRemove: (index: number) => void;
}

function ColumnRow({
  column,
  index,
  isFirst,
  isLast,
  onChange,
  onMove,
  onRemove,
}: ColumnRowProps) {
  const headerName = column.header || `Column ${index + 1}`;

  function setField(fieldIndex: number, value: ExportFieldKey) {
    onChange(index, {
      fields: column.fields.map((f, i) => (i === fieldIndex ? value : f)),
    });
  }
  function addField() {
    onChange(index, { fields: [...column.fields, "contact.name"] });
  }
  function removeField(fieldIndex: number) {
    if (column.fields.length <= 1) return;
    onChange(index, { fields: column.fields.filter((_, i) => i !== fieldIndex) });
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-xl border border-border-muted glass-surface-subtle p-4",
        !column.visible && "opacity-60",
      )}
    >
      {/* items-end plus the h-10 wrappers keep the checkbox and controls centred
          on the input now that the "Export Column" label adds height above it. */}
      <div className="flex items-end gap-3">
        <div className="flex h-10 items-center">
          <Checkbox
            checked={column.visible}
            onCheckedChange={(checked) => onChange(index, { visible: checked === true })}
            aria-label={`Include column ${headerName}`}
          />
        </div>
        <FormField
          label="Export Column"
          value={column.header}
          onChange={(e) => onChange(index, { header: e.target.value })}
          aria-label={`Header for column ${index + 1}`}
          placeholder="Column header"
          className="max-w-xs"
        />
        <div className="ml-auto flex h-10 items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onMove(index, -1)}
            disabled={isFirst}
            aria-label={`Move ${headerName} up`}
          >
            <ChevronUp aria-hidden className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onMove(index, 1)}
            disabled={isLast}
            aria-label={`Move ${headerName} down`}
          >
            <ChevronDown aria-hidden className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost-danger"
            size="icon"
            onClick={() => onRemove(index)}
            aria-label={`Remove ${headerName}`}
          >
            <X aria-hidden className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 pl-8">
        <Label className="mx-0">
          {column.fields.length > 1 ? "Combined list fields" : "List Field"}
        </Label>
        <div className="flex flex-col gap-2">
          {column.fields.map((field, fieldIndex) => (
            <div key={fieldIndex} className="flex items-center gap-2">
              <SelectInput
                options={FIELD_OPTIONS}
                value={field}
                onValueChange={(value) => setField(fieldIndex, value as ExportFieldKey)}
                aria-label={`Field ${fieldIndex + 1} for ${headerName}`}
                className="max-w-md"
              />
              {column.fields.length > 1 ? (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeField(fieldIndex)}
                  aria-label={`Remove field ${fieldIndex + 1} from ${headerName}`}
                >
                  <X aria-hidden className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" onClick={addField}>
            <Plus aria-hidden className="h-4 w-4" />
            Combine another field
          </Button>
          {column.fields.length > 1 ? (
            <div className="flex items-center gap-2 type-body-sm text-text-muted">
              <span>Join with</span>
              <Input
                value={column.separator}
                onChange={(e) => onChange(index, { separator: e.target.value })}
                aria-label={`Separator for ${headerName}`}
                className="h-8 w-24"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
